import { useState, useEffect } from 'react'
import {
  Table,
  Typography,
  DateField,
  Space,
  Input,
  useTable,
  Form,
  Icons,
  PageHeader,
  getDefaultSortOrder,
  Tooltip,
  Popconfirm,
  ShowButton,
  Button,
  message,
  ExportButton,
  Row,
  ErrorComponent,
  Badge,
} from '@pankod/refine-antd'
import { useExport, useGetIdentity, useList, useNavigation } from '@pankod/refine-core'
import { IEvaluationCandidate, ISection } from 'interfaces'
import metaData from 'metadata/candidate'
import { getDetailsById, getEvaluationDetails } from 'gql/query'
import { deleteCandidate } from 'gql/mutations'
import { deleteReportCardLinks } from 'helpers/evaluations/reportCardeDeletion'
import { getAllUsers } from 'users'
import { useUserContext } from 'context/user'
import CandidateResultFilter from 'components/filters/candidateResultFilter'
import { changeBulkReportCardVisibility, updateReportCardVisibility } from 'helpers/evaluations/reportCardVisibility'
import { getMobileReportCardDownloadUrl, hasGeneratedReportCard } from 'helpers/reportCardDownload'
import './style.css'
import { useBloomsLevel } from 'utility/masterData'
import { formatScorecardDecimal } from 'helpers/scorecardFormat'
type candidateListPageProps = {}

const { Text } = Typography

export const CandidatesListPage: React.FC<candidateListPageProps> = () => {
  const { userRole } = useUserContext()
  const { show, push } = useNavigation()
  const [candidateReportcardDetails, setCandidateReportcardDetails] = useState<any>([])
  const [allUsers, setAllUsers] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRowDetails, setSelectedRowDetails] = useState([])
  const [bulkVisibilityLoading, setBulkVisibilityLoading] = useState<boolean>(false)
  const bloomlevelSelectProps = useBloomsLevel(true)
  const allBloomsLevels = bloomlevelSelectProps?.queryResult?.data?.data || []
  const {
    tableQueryResult: listOfCandidatesQueryResult,
    setFilters,
    searchFormProps,
    sorter,
    tableProps,
    filters,
  } = useTable<IEvaluationCandidate>({
    resource: 'evaluation_details',
    metaData: metaData,
    initialSorter: [{ field: 'updated_at', order: 'desc' }],
    onSearch(data: any) {
      return [
        {
          operator: 'or',
          value: [
            { field: 'candidate_name', operator: 'contains', value: `%${data?.name}%` },
            { field: 'application_id', operator: 'contains', value: `%${data?.name}%` },
          ],
        },
      ]
    },
  })

  //fetching all section details
  const { data: listOfSections, refetch } = useList<ISection>({
    resource: 'sections',
    metaData: {
      fields: ['id', 'name'],
    },
    queryOptions: {
      enabled: true,
      staleTime: Infinity,
    },
  })
  //filtering all section names
  let allSectionLabels = listOfSections?.data?.map((item: any) => item?.name) || []

  const sectionPriorityList = [
    'Subject Expertise (SE)',
    'Pedagogical Content Knowledge (PCK)',
    'General Pedagogy (GP)',
    'Professional and core competencies (PC)',
    'Subject Pedagogical Content Knowledge',
    'Continuous and Comprehensive Evaluation (CCE)',
    'Teacher Perspectives and Professional Competencies (TP&PC)',
  ]
  //sorting all section names
  allSectionLabels = allSectionLabels.sort((a: string, b: string) => {
    const indexA = sectionPriorityList.indexOf(a)
    const indexB = sectionPriorityList.indexOf(b)
    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
  })

  const updateFilters = (filters: any, clear?: boolean) => {
    if (clear) {
      setFilters(filters, 'replace')
    } else {
      setFilters(filters, 'merge')
    }
  }
  const getCandidateEvaluationDetails = async () => {
    //fetching all the evaluation details
    const getEvaluationDetails = await getDetailsById(
      null,
      `candidate_report_data`,
      `evaluation_id`,
      `{score_card_url candidate_id candidate_name section_level_details evaluation_id is_visible blooms_level_details}`
    )
    //fetching all the users list to show the evaluator name
    const getAllUsersDetails: any = await getAllUsers()
    setCandidateReportcardDetails(getEvaluationDetails)
    if (getAllUsersDetails && getAllUsersDetails?.users?.length > 0) setAllUsers(getAllUsersDetails?.users || [])
  }
  useEffect(() => {
    getCandidateEvaluationDetails()
  }, [])
  //Exporting details
  const { triggerExport: exportEvaluationDetails, isLoading: candidateOverViewExportLoading } = useExport<IEvaluationCandidate>({
    resourceName: 'evaluation_details',
    metaData,
    filters,
    sorter,
    exportOptions: {
      filename: `candidate-over-view_${Date.now()}`,
      headers: [
        'Candidate_Name',
        'TQ_ID',
        'Obtained_Score',
        'Total_Score',
        'Digital_Badge_Eligibility',
        'Score_Card_Link',
        'Test_Type',
        'Paper_Name',
        'Paper_Id',
        'Test_Track_Name',
        'Test_Track_Id',
        'Test_Event_Name',
        'Test_Event_Id',
        'Product_Name',
        'Product_Id',
        'Evaluation_Name',
        'Evaluation_Id',
        'Evaluation_Request_Name',
        'Evaluation_Request_Id',
        ...allBloomsLevels.flatMap(blooms => [`${blooms.name}_Total`, `${blooms.name}_Obtained`, `${blooms.name}_Percentage`]),
        ...allSectionLabels.flatMap(label => [`${label}_Total`, `${label}_Obtained`, `${label}_Percentage`]),
      ],
    },
    maxItemCount: 100000,
    mapData: record => {
      if (!record) return
      const candidateDetails = candidateReportcardDetails.find(
        ({ candidate_id, evaluation_id }: any) => candidate_id === record.application_id && evaluation_id === record?.evaluation_id
      )
      const sectionData = candidateDetails?.section_level_details
      const candidateBloomsData = candidateDetails?.blooms_level_details
      const digitalBadgeEligibility = !record?.tq
        ? 'N/A'
        : !candidateDetails
          ? 'Not Generated'
          : sectionData?.All_Items_Greater_Than30 && (Number(sectionData?.total_marks) / sectionData?.total_paper_score) * 100 >= 50
            ? 'Yes'
            : 'No'

      const exportRow: Record<string, any> = {
        Candidate_Name: record?.candidate_name || 'Not Found',
        TQ_ID: record?.application_id || 'Not Found',
        Obtained_Score: record?.total_score || 'Not Found',
        Total_Score: record?.evaluation?.paper?.product?.total_marks || 'Not Found',
        Digital_Badge_Eligibility: digitalBadgeEligibility,
        Score_Card_Link:
          candidateDetails && hasGeneratedReportCard(candidateDetails)
            ? getMobileReportCardDownloadUrl(record?.application_id, record?.evaluation?.paper?.product?.type?.toLowerCase() || 'tq')
            : 'Not Generated',
        Test_Type: record?.evaluation?.paper?.product?.type?.toUpperCase() || 'TQ',
        Paper_Name:
          record?.evaluation?.paper?.paper_id && record?.evaluation?.paper?.paper_smart_id
            ? `#${record?.evaluation?.paper?.paper_id}-${record?.evaluation?.paper?.paper_smart_id}`
            : 'Not Found',
        Paper_Id: record?.evaluation?.paper?.id || 'Not Found',
        Test_Track_Name: record?.evaluation?.paper?.test_track?.name || 'Not Found',
        Test_Track_Id: record?.evaluation?.paper?.test_track?.id || 'Not Found',
        Test_Event_Name: record?.evaluation?.paper?.test_event?.name || 'Not Found',
        Test_Event_Id: record?.evaluation?.paper?.test_event?.id || 'Not Found',
        Product_Name: record?.evaluation?.paper?.product?.name || 'Not Found',
        Product_Id: record?.evaluation?.paper?.product?.id || 'Not Found',
        Evaluation_Name: record?.evaluation?.name || 'Not Found',
        Evaluation_Id: record?.evaluation?.id || 'Not Found',
        Evaluation_Request_Name:
          record?.evaluation?.evaluation_request?.request_id && record?.evaluation?.evaluation_request?.name
            ? `#${record?.evaluation?.evaluation_request?.request_id}-${record?.evaluation?.evaluation_request?.name}`
            : 'Not Found',
        Evaluation_Request_Id: record?.evaluation?.evaluation_request?.id || 'Not Found',
      }
      allSectionLabels.forEach(label => {
        const sectionIndex = sectionData?.labelsOfSection?.indexOf(label)
        exportRow[`${label}_Total`] = sectionIndex >= 0 ? sectionData?.sectionWiseTotal?.[sectionIndex] ?? 'N/A' : 'N/A'
        exportRow[`${label}_Obtained`] = sectionIndex >= 0 ? sectionData?.sectionWiseTotalObtained?.[sectionIndex] ?? 'N/A' : 'N/A'
        exportRow[`${label}_Percentage`] = sectionIndex >= 0 ? sectionData?.sectionWisePercentage?.[sectionIndex] ?? 'N/A' : 'N/A'
      })
      allBloomsLevels.forEach(blooms => {
        const bloomsIndex = candidateBloomsData?.labelsOfSection?.indexOf(blooms?.name)
        const exists = bloomsIndex >= 0
        // console.log(exists,"Blooms")
        const total = exists ? candidateBloomsData?.sectionWiseTotal?.[bloomsIndex] ?? null : null
        const obtained = exists ? candidateBloomsData?.sectionWiseTotalObtained?.[bloomsIndex] ?? null : null
        let percentage = 'N/A'
        if (exists && total && obtained !== null) {
          const rawPercent = (obtained / total) * 100
          percentage = formatScorecardDecimal(rawPercent)
        }
        exportRow[`${blooms.name}_Total`] = total ?? 'N/A'
        exportRow[`${blooms.name}_Obtained`] = obtained ?? 'N/A'
        exportRow[`${blooms.name}_Percentage`] = percentage
      })
      return exportRow
    },
  })
  const candidatesList = listOfCandidatesQueryResult?.data?.data
  const downlaodScoreCard = (candidateId: string, testType: string) => {
    const url = getMobileReportCardDownloadUrl(candidateId, testType)
    window.open(url, '_blank')
  }

  const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: any) => {
    setSelectedRowKeys(newSelectedRowKeys)
    setSelectedRowDetails(selectedRows)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: any) => ({
      disabled: !record?.is_eligible_for_result || !record?.is_attended,
    }),
  }

  return userRole === 'admin' || userRole === 'productmanager' || userRole === 'deliverymanager' ? (
    <PageHeader
      title={
        <span>
          Candidate Details Overview{' '}
          <Badge
            size="small"
            count={listOfCandidatesQueryResult?.data?.total || 0}
            overflowCount={99999}
            style={{ background: '#6659ff', marginLeft: 8, verticalAlign: 'middle' }}
          />
        </span>
      }
    >
      {/* Search bar */}
      <Row gutter={[24, 24]} style={{ marginLeft: '2px' }}>
        <Form {...searchFormProps}>
          <Space>
            <Form.Item name="name">
              <Input
                placeholder="Search by Id or Name"
                onChange={() => searchFormProps.form?.submit()}
                allowClear
                prefix={<Icons.SearchOutlined style={{ cursor: 'pointer' }} />}
              />
            </Form.Item>
          </Space>
        </Form>
        <ExportButton
          style={{ marginRight: '10px', marginLeft: '10px' }}
          onClick={exportEvaluationDetails}
          loading={candidateOverViewExportLoading}
        />
        {selectedRowKeys && selectedRowKeys.length > 0 && (
          <Button
            loading={bulkVisibilityLoading}
            icon={<Icons.EyeTwoTone />}
            onClick={async () => {
              setBulkVisibilityLoading(true)
              await changeBulkReportCardVisibility(selectedRowDetails, candidateReportcardDetails)
              await getCandidateEvaluationDetails()
              setBulkVisibilityLoading(false)
            }}
          >
            Change Visibility
          </Button>
        )}
      </Row>
      <CandidateResultFilter updateFilter={updateFilters} filters={filters} />
      {/* Table */}
      <Table rowKey="id" key="table" {...tableProps} bordered size="small" dataSource={candidatesList} rowSelection={rowSelection}>
        <Table.Column
          title="Candidate ID"
          key="application_id"
          dataIndex="application_id"
          fixed="left"
          sortOrder={getDefaultSortOrder('user_id', sorter)}
          render={(_, record: any) => {
            return (
              <Text strong code>
                {record?.application_id || 'N/A'}
              </Text>
            )
          }}
        />
        <Table.Column
          title="Candidate Name"
          key="candidate_name"
          dataIndex="candidate_name"
          render={(_, record: any) => {
            return (
              <Text style={{ width: 150 }} ellipsis={{ tooltip: `${record?.username}` }}>
                {record?.candidate_name || 'N/A'}
              </Text>
            )
          }}
        />
        <Table.Column
          title="Total Score"
          key="total_score"
          dataIndex="total_score"
          render={(_, record: any) => {
            return (
              <Text style={{ width: 150 }}>
                {record?.total_score || 'NA'} / {record?.evaluation?.paper?.product?.total_marks || 'NA'}
              </Text>
            )
          }}
        />
        {/* Digital badge eligibility showing */}
        <Table.Column
          title="Digital Badge"
          key="digital_badge"
          dataIndex="application_id"
          render={(_, record: any) => {
            // finding candidate details from the report table
            const candidateDetails = candidateReportcardDetails.find(
              ({ candidate_id, evaluation_id }: any) => candidate_id === record.application_id && evaluation_id === record?.evaluation_id
            )
            // tq and availability checking?
            return record?.is_attended &&
              record?.is_eligible_for_result &&
              record?.evaluation?.paper?.product?.tq &&
              candidateDetails?.section_level_details ? (
              candidateDetails?.section_level_details?.All_Items_Greater_Than30 &&
                (Number(candidateDetails?.section_level_details?.total_marks) / candidateDetails?.section_level_details?.total_paper_score) * 100 >=
                50 ? (
                <Tooltip title={`Eligible`}>
                  <Icons.LikeFilled style={{ fontSize: 16, color: '#7ba177' }} />
                </Tooltip>
              ) : (
                <Tooltip title={`Not Eligible`}>
                  <Icons.DislikeFilled style={{ fontSize: 16, color: '#1890ff' }} />
                </Tooltip>
              )
            ) : (
              <Tooltip
                title={
                  !record?.is_eligible_for_result || !record?.is_attended
                    ? 'The candidate is not eligible for a digital badge due to either non-attendance or malpractice during the examination'
                    : !candidateDetails
                      ? 'Result is missing. Please generate the result or contact the AMS admin for assistance.'
                      : 'Note: Only TQ-type products are eligible for a Digital Badge.'
                }
              >
                <Icons.StopOutlined style={{ fontSize: 16, color: '#c96969' }} />
              </Tooltip>
            )
          }}
        />
        {/* Score card */}
        <Table.Column
          title="Score Card"
          key="application_id"
          dataIndex="application_id"
          render={(_, record: any) => {
            const candidateDetails = candidateReportcardDetails.find(
              ({ candidate_id, evaluation_id }: any) => candidate_id === record.application_id && evaluation_id === record?.evaluation_id
            )
            return hasGeneratedReportCard(candidateDetails) ? (
              <>
                <Tooltip title={`View Score card - ${candidateDetails.candidate_name}`}>
                  {/* opening the scorecard in another tab */}
                  <a
                    href={getMobileReportCardDownloadUrl(
                      candidateDetails?.candidate_id,
                      record?.evaluation?.paper?.product?.type?.toLowerCase() || 'tq'
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icons.FilePdfFilled style={{ fontSize: 16, color: '#80bde0' }} />
                  </a>
                </Tooltip>
                {/* RC Link deletion */}
                <Popconfirm
                  title={`Are you sure, do you want to delete this candidate's score card link?`}
                  onConfirm={() =>
                    deleteReportCardLinks({ candidateId: candidateDetails?.candidate_id, url: candidateDetails?.score_card_url })
                      .then(async (res: any) => {
                        if (res.status) {
                          const candidateReportData = candidateReportcardDetails.map((item: any) =>
                            item.candidate_id === candidateDetails?.candidate_id ? { ...item, score_card_url: null } : item
                          )
                          setCandidateReportcardDetails(candidateReportData)
                          message.success(res.message || 'Items deleted successfully..')
                        } else {
                          message.error(res.message || 'Unable to complete the deletion!')
                        }
                      })
                      .catch(err => message.error('server down!'))
                  }
                  okText="Yes"
                  cancelText="No"
                >
                  <Icons.DeleteOutlined style={{ marginLeft: '8px', color: '#c96969', cursor: 'pointer' }} />
                </Popconfirm>
              </>
            ) : (
              <Tooltip
                title={
                  !record?.is_eligible_for_result || !record?.is_attended
                    ? 'The candidate is not eligible for a scorecard due to either non-attendance or malpractice during the examination'
                    : !candidateDetails
                      ? 'Result is missing. Please generate the result or contact the AMS admin for assistance.'
                      : 'Result is generated, but the scorecard link has not been generated yet.'
                }
              >
                {!candidateDetails || !record?.is_eligible_for_result || !record?.is_attended ? (
                  <Icons.StopOutlined style={{ fontSize: 16, color: '#c96969' }} />
                ) : (
                  <Icons.FilePdfFilled
                    onClick={() => window.open(`${process.env.REACT_APP_PRODUCTION_URL}/candidatereport/${record?.application_id}`, '_blank')}
                    // onClick={() => window.open(`http://localhost:3000/candidatereport/${record?.application_id}`, '_blank')}
                    style={{ fontSize: 16, color: '#80bde0' }}
                  />
                )}
              </Tooltip>
            )
          }}
        />
        <Table.Column
          title="Is Attended"
          key="is_attended"
          dataIndex="is_attended"
          render={(_, record: any) => (
            <>
              {record?.is_attended ? (
                <Icons.LikeFilled style={{ fontSize: 16, color: '#7ba177' }} />
              ) : (
                <Icons.DislikeFilled style={{ fontSize: 16, color: '#c96969' }} />
              )}
            </>
          )}
        />
        <Table.Column
          title="Is Eligible for Result"
          key="is_eligible_for_result"
          dataIndex="is_eligible_for_result"
          render={(_, record: any) => (
            <>
              {record?.is_eligible_for_result ? (
                <Icons.LikeFilled style={{ fontSize: 16, color: '#7ba177' }} />
              ) : (
                <Icons.DislikeFilled style={{ fontSize: 16, color: '#c96969' }} />
              )}
            </>
          )}
        />
        <Table.Column
          title="Type"
          key="tq"
          dataIndex="tq"
          render={(_, record: any) => <Text>{record?.evaluation?.paper?.product?.type?.toUpperCase()}</Text>}
        />
        <Table.Column
          title="Test Track"
          key="test_track"
          dataIndex="id"
          render={(_, record: any) => <Text>{record?.evaluation?.paper?.test_track?.name || 'N/A'}</Text>}
        />
        <Table.Column
          title="Paper"
          key="paper_id"
          dataIndex="paper_id"
          render={(_, record: any) => {
            return record?.evaluation?.paper?.id ? (
              <>
                <ShowButton
                  size="small"
                  type="link"
                  icon={null}
                  recordItemId={record?.evaluation?.paper?.id}
                  onClick={() => {
                    show('papers', record?.evaluation?.paper?.id)
                  }}
                >
                  <Text style={{ color: '#4e7191' }}>{`#${record?.evaluation?.paper?.paper_id}-${record?.evaluation?.paper?.paper_smart_id}`}</Text>
                </ShowButton>
              </>
            ) : (
              <>
                <Text>{'N/A'}</Text>
              </>
            )
          }}
        />

        <Table.Column
          title="Test Event"
          key="test_event"
          dataIndex="test_event"
          render={(_, record: any) => {
            return record?.evaluation?.paper?.test_event?.id ? (
              <>
                <ShowButton
                  size="small"
                  type="link"
                  icon={null}
                  recordItemId={record?.evaluation?.paper?.test_event?.id}
                  onClick={() => {
                    show('test_events', record?.evaluation?.paper?.test_event?.id)
                  }}
                >
                  <Text style={{ color: '#4e7191' }}>{record?.evaluation?.paper?.test_event?.name}</Text>
                </ShowButton>
              </>
            ) : (
              <>
                <Text>{'N/A'}</Text>
              </>
            )
          }}
        />
        <Table.Column
          title="Product"
          key="product_name"
          dataIndex="product_name"
          render={(_, record: any) => {
            return record?.evaluation?.paper?.product?.id ? (
              <>
                <ShowButton
                  size="small"
                  type="link"
                  icon={null}
                  recordItemId={record?.evaluation?.paper?.product?.id}
                  onClick={() => {
                    show('products', record?.evaluation?.paper?.product?.id)
                  }}
                >
                  <Text style={{ color: '#4e7191' }}>{record?.evaluation?.paper?.product?.name}</Text>
                </ShowButton>
              </>
            ) : (
              <Text>{'N/A'}</Text>
            )
          }}
        />
        <Table.Column
          title="Evaluation"
          key="evaluation_name"
          dataIndex="id"
          render={(_, record: any) => {
            return record?.evaluation?.id ? (
              <>
                <ShowButton
                  size="small"
                  type="link"
                  icon={null}
                  recordItemId={record?.evaluation?.id}
                  onClick={() => {
                    show('evaluations', record?.evaluation?.id)
                  }}
                >
                  <Text style={{ color: '#4e7191' }}>{record?.evaluation?.name || 'N/A'}</Text>
                </ShowButton>
              </>
            ) : (
              <Text>{'N/A'}</Text>
            )
          }}
        />
        <Table.Column
          title="Evaluation Request"
          key="evaluation_request"
          dataIndex="id"
          render={(_, record: any) => {
            return record?.evaluation?.evaluation_request?.id ? (
              <>
                <ShowButton
                  size="small"
                  type="link"
                  icon={null}
                  recordItemId={record?.evaluation?.id}
                  onClick={() => {
                    show('evaluation_requests', record?.evaluation?.evaluation_request?.id)
                  }}
                >
                  <Text style={{ color: '#4e7191' }}>
                    {`#${record?.evaluation?.evaluation_request?.request_id}-${record?.evaluation?.evaluation_request?.name}` || 'N/A'}
                  </Text>
                </ShowButton>
              </>
            ) : (
              <Text>{'N/A'}</Text>
            )
          }}
        />
        <Table.Column
          title="Evaluator"
          key="evaluator"
          dataIndex="id"
          render={(_, record: any) => {
            const evaluator: any = record?.evaluation?.created_by ? allUsers.find((user: any) => user.id === record?.evaluation?.created_by) : null
            return <Text>{evaluator && evaluator?.displayName ? evaluator?.displayName?.toUpperCase() : 'N/A'}</Text>
          }}
        />
        <Table.Column
          title="Test Date"
          key="test_date"
          dataIndex="test_date"
          render={(value, record: any) => {
            const candidateDetails = candidateReportcardDetails.find(
              ({ candidate_id, evaluation_id }: any) => candidate_id === record.application_id && evaluation_id === record?.evaluation_id
            )
            if (candidateDetails && candidateDetails?.section_level_details?.test_date) {
              const parsedDate = new Date(candidateDetails?.section_level_details?.test_date.split('-').reverse().join('-'))
              return parsedDate ? <DateField value={parsedDate} format="LL" /> : <Text>N/A</Text>
            } else {
              return <Text>N/A</Text>
            }
          }}
        />
        <Table.Column
          title="Evaluation Date"
          key="updated_at"
          dataIndex="updated_at"
          render={(value, record) => {
            return <DateField value={value} format="LL" />
          }}
        />
        <Table.Column
          title="Actions"
          key="id"
          dataIndex="id"
          width={200}
          render={(_, record: any) => {
            const candidateDetails = candidateReportcardDetails.find(
              ({ candidate_id, evaluation_id }: any) => candidate_id === record.application_id && evaluation_id === record?.evaluation_id
            )
            let is_visible = candidateDetails?.is_visible || false
            return (
              <div style={{ padding: '10px', display: 'flex', gap: '3px' }}>
                <Popconfirm
                  title="Are you sure to delete this candidate?"
                  onConfirm={() => {
                    if (record && record?.id && record?.application_id) {
                      deleteCandidate(record.id, record.application_id)
                        .then(async (res: any) => {
                          if (res) {
                            await listOfCandidatesQueryResult.refetch()
                            message.success(
                              `Candidate (${record?.candidate_name || record?.application_id}) evaluation details deleted successfully...`
                            )
                          } else {
                            message.error('deletion failed!')
                          }
                        })
                        .catch(err => {
                          console.log(err, 'ERR')
                          message.error('Something went wrong, please try again later...')
                        })
                    }
                  }}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button icon={<Icons.DeleteOutlined style={{ color: '#c44e3f' }} />} />
                </Popconfirm>
                {candidateDetails && record?.is_eligible_for_result && record?.is_attended && is_visible && (
                  <>
                    <Button
                      icon={
                        <Icons.DownloadOutlined
                          style={{ color: '#6a86ab' }}
                          onClick={async () => downlaodScoreCard(record?.application_id, record?.evaluation?.paper?.product?.type?.toLowerCase())}
                        />
                      }
                    />
                  </>
                )}
                <>
                  <Popconfirm
                    title={
                      is_visible
                        ? 'Are you sure you want to hide the scorecard from the candidate?'
                        : 'Are you sure you want to make the scorecard visible to the candidate?'
                    }
                    onConfirm={async () => {
                      await updateReportCardVisibility([{ candidate_id: record?.application_id, visibility: is_visible }])
                      if (record) {
                        await getCandidateEvaluationDetails()
                      }
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      icon={record?.is_attended && is_visible && record?.is_eligible_for_result ? <Icons.EyeFilled /> : <Icons.EyeInvisibleFilled />}
                      style={{
                        color: record?.is_attended && is_visible && record?.is_eligible_for_result ? '#80bde0' : '#fa7070',
                        marginLeft: '5px',
                        opacity: record?.is_attended && record?.is_eligible_for_result && candidateDetails ? '' : '0.5',
                      }}
                      disabled={!record?.is_attended || !record?.is_eligible_for_result || !candidateDetails}
                      className={!is_visible && candidateDetails && record?.is_attended && record?.is_eligible_for_result ? 'button-animate' : ''}
                    />
                  </Popconfirm>
                </>
              </div>
            )
          }}
        />
      </Table>
    </PageHeader>
  ) : (
    <ErrorComponent />
  )
}

export * from './test_event_candidates'

// -------------------------------------------------OLD ONE--------------------------------------------------------------------
// import { useState, useEffect } from 'react'
// import {
//   Table,
//   Typography,
//   Modal,
//   Button,
//   Tooltip,
//   DateField,
//   Tag,
//   Space,
//   Row,
//   notification,
//   ImportButton,
//   Input,
//   useTable,
//   Form,
//   Icons,
//   PageHeader,
//   getDefaultSortOrder,
// } from '@pankod/refine-antd'
// import { List } from 'antd'
// import { useImport, useGetIdentity } from '@pankod/refine-core'
// import { exportToExcel } from 'exportToExcel'
// import { ICandidateRegistration, ICandidate } from 'interfaces'
// import metaData from 'metadata/candidate'

// type candidateListPageProps = {}

// const { Text } = Typography

// export const CandidatesListPage: React.FC<candidateListPageProps> = ({}) => {
//   const { data: user } = useGetIdentity()
//   const [exportData, setExportData] = useState<any>()
//   const {
//     tableQueryResult: listOfCandidatesQueryResult,
//     setFilters,
//     searchFormProps,
//     sorter,
//     tableProps,
//   } = useTable<ICandidate>({
//     resource: 'candidates',
//     metaData,
//     initialSorter: [{ field: 'user_id', order: 'desc' }],
//     onSearch(data: any) {
//       return [
//         {
//           key: 'filter_by_name',
//           operator: 'or',
//           value: [
//             { field: 'first_name', operator: 'contains', value: `%${data?.name}%` },
//             { field: 'last_name', operator: 'contains', value: `%${data?.name}%` },
//             { field: 'user_id', operator: 'contains', value: `%${data?.name}%` },
//           ],
//         },
//       ]
//     },
//   })
//   // const {tableQueryResult:listOfCandidatesByTestEventQueryResult} = useTable<ITestEventCandidates>({resource:'test_event_candidatess', metaData:testEventCandidateMetaData, queryOptions:{enabled:!!testEventId})

//   const candidatesList = listOfCandidatesQueryResult?.data?.data

//   const uploadCandidateList: any[] = []

//   const { inputProps, handleChange, isLoading, mutationResult } = useImport({
//     resourceName: 'candidates',
//     paparseOptions: { skipEmptyLines: true },
//     mapData: data => ({ ...data, created_by: user?.id }),
//     onFinish: results => {
//       const { errored, succeeded } = results
//       if (errored?.length > 0) {
//         notification.error({
//           message: 'Error Occured',
//           description: `${errored?.[0]?.request?.length} candidates data could not be uploaded`,
//           duration: 5,
//         })
//       }
//       if (succeeded?.length > 0) {
//         notification.success({
//           message: 'Successfully Uploaded',
//           description: `${succeeded?.[0]?.response?.length} candidates data were uploaded`,
//           duration: 5,
//         })
//       }
//       console.log('ONFINISH RESULTSS', results)
//     },
//   })

//   return (
//     <PageHeader
//       title="Candidates"
//       extra={[
//         <Button type="primary">
//           <Input {...inputProps} />
//         </Button>,
//       ]}
//     >
//       <Form {...searchFormProps} key="form">
//         <Space>
//           <Form.Item name="name">
//             <Input placeholder="Search by Name or User ID" />
//           </Form.Item>
//           <Button type="primary" icon={<Icons.SearchOutlined />} onClick={searchFormProps.form?.submit}>
//             Search
//           </Button>
//           <Button
//             onClick={() => {
//               searchFormProps?.form?.setFieldsValue({ name: '' })
//               searchFormProps.form?.submit()
//             }}
//           >
//             Clear
//           </Button>
//         </Space>
//       </Form>
//       <Table
//         rowKey="id"
//         key="table"
//         {...tableProps}
//         bordered
//         size="small"
//         dataSource={candidatesList}
//         rowSelection={{
//           onChange(selectedRowKeys, selectedRows, info) {
//             console.log('on each selectbox selection', selectedRowKeys, selectedRows, info)
//             setExportData(selectedRows)
//           },
//         }}
//       >
//         <Table.Column
//           title="User ID"
//           key="user_id"
//           dataIndex="user_id"
//           fixed="left"
//           sortOrder={getDefaultSortOrder('user_id', sorter)}
//           render={(_, record: any) => {
//             return (
//               <Text strong code>
//                 {record?.user_id}
//               </Text>
//             )
//           }}
//         />
//         <Table.Column
//           title="Username"
//           key="username"
//           dataIndex="username"
//           render={(_, record: any) => {
//             return (
//               <Text style={{ width: 150 }} ellipsis={{ tooltip: `${record?.username}` }}>
//                 {record?.username}
//               </Text>
//             )
//           }}
//         />
//         <Table.Column
//           title="Name"
//           key="full_name"
//           dataIndex="first_name"
//           render={(_, record: any) => {
//             return (
//               <Text style={{ width: 150 }} ellipsis={{ tooltip: `${record?.first_name} ${record?.last_name}` }}>
//                 {record?.first_name} {record?.last_name}
//               </Text>
//             )
//           }}
//         />
//         <Table.Column title="Email ID" key="email_id" dataIndex="email_id" render={(_, record: any) => <Text>{record?.email_id}</Text>} />
//         <Table.Column
//           title="Phone Number"
//           key="phone_number"
//           dataIndex="phone_number"
//           render={(_, record: any) => <Text>{record?.phone_number}</Text>}
//         />

//         {/* <Table.Column
//           key="test_event"
//           dataIndex="test_event"
//           render={(_, record: any) => {
//             return <Text>{record?.test_event?.name}</Text>
//           }}
//         />
//         <Table.Column
//           key="date"
//           dataIndex="date"
//           render={(value, record) => {
//             return <DateField value={value} format="LL" />
//           }}
//         />
//         <Table.Column
//           key="time_slot"
//           render={(_, record: any) => {
//             return (
//               <Tag>
//                 <Space>
//                   <DateField value={record?.start_time} format="LT" />
//                   <span>To</span>
//                   <DateField value={record?.end_time} format="LT" />
//                 </Space>
//               </Tag>
//             )
//           }}
//         /> */}
//       </Table>
//     </PageHeader>
//   )
// }

// export * from './test_event_candidates'
